import { motion, useReducedMotion, type Variants } from 'motion/react';
import { EASE } from '../lib/motion';

type SplitWordsProps = {
  text: string;
  /** Play immediately when true (hero). Omit to play on scroll into view. */
  play?: boolean;
  delay?: number;
  stagger?: number;
  className?: string;
};

const word: Variants = {
  hidden: { y: '112%', rotate: 5 },
  show: { y: '0%', rotate: 0, transition: { duration: 1.05, ease: EASE } },
};

/**
 * Masked word-by-word rise. Each word slides up out of its own clipping box,
 * which reads as the headline being typeset in front of you. The mask keeps
 * descender room (pb / -mb) so letters like "g" and "p" are never clipped.
 * Screen readers get the plain sentence.
 *
 * The in-view trigger lives on the unclipped wrapper: the words themselves
 * start fully hidden by their masks, so an observer on them would never fire.
 */
export function SplitWords({
  text,
  play,
  delay = 0,
  stagger = 0.07,
  className,
}: SplitWordsProps) {
  const reduce = useReducedMotion();
  const words = text.split(' ');
  const trigger =
    play !== undefined
      ? { animate: play ? 'show' : 'hidden' }
      : { whileInView: 'show', viewport: { once: true, amount: 0.5 } };

  return (
    <motion.span
      className={className}
      initial={reduce ? false : 'hidden'}
      {...trigger}
      variants={{
        hidden: {},
        show: {
          transition: { staggerChildren: stagger, delayChildren: delay },
        },
      }}
    >
      <span className="sr-only">{text}</span>
      <span aria-hidden>
        {words.map((w, i) => (
          <span key={`${w}-${i}`}>
            <span className="-mb-[0.16em] inline-block overflow-hidden pb-[0.16em] align-top">
              <motion.span
                className="inline-block will-change-transform"
                variants={word}
              >
                {w}
              </motion.span>
            </span>
            {i < words.length - 1 && ' '}
          </span>
        ))}
      </span>
    </motion.span>
  );
}
